import { createColumnHelper } from "@tanstack/react-table";
import {
  AttendancePunch,
  AttendanceTable,
  TerminatedEmployeeDetails,
} from "../../interfaces/app_types";
import { formatTime, getDateFromString } from "../funcs/time";
import AttendanceButtonModal from "../../components/attendance/AttendanceButtonModal";
import { convertDecimalToHoursMinutes, roundDigits } from "../funcs/common";
import TerminateEmployeesButton from "../../components/salary/TerminateEmployeesButton";

const getP = (working_hrs) => {
  if (parseFloat(working_hrs) >= 8.5) {
    const pFormula = Math.round((parseFloat(working_hrs) - 8.5) * 2) / 2;
    return `P${Math.min(pFormula, 3)}`;
  } else {
    return roundDigits(parseFloat(working_hrs), 1);
  }
};

const columnHelper = createColumnHelper<AttendanceTable>();
const columnMonthHelper = createColumnHelper<AttendancePunch>();
const employeeColumnHelper = createColumnHelper<TerminatedEmployeeDetails>();
const isMobile = window.innerWidth < 767.98;
const presentText = isMobile ? "P" : "Present";
const absentText = isMobile ? "A" : "Absent";

const getMonthStatusClass = (status: string) => {
  if (status === "A") return "absent";
  if (/^P[0-3]$/.test(status)) return status.toLowerCase();
  return "partial";
};

export const AttendanceColumns = [
  columnHelper.group({
    header: "Employee",

    columns: [
      columnHelper.accessor((row) => row?.emp_code, {
        header: "ID",
        enableColumnFilter: false,
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor((row) => row?.emp_name, {
        header: "Name",
        enableGlobalFilter: true,
        enableColumnFilter: false,
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor((row) => row?.emp_code, {
        id: "Month",
        enableColumnFilter: false,
        cell: (props) => (
          <AttendanceButtonModal empCode={parseInt(props.getValue())} />
        ),
      }),
    ],
  }),
  columnHelper.group({
    header: "Present/Absent",
    footer: (props) => props.column.id,
    columns: [
      columnHelper.accessor((row) => row?.in_time, {
        id: "Status",
        enableColumnFilter: false,

        cell: (props) => (
          <div
            className={`attd-status attd-status--${
              props.getValue() ? "present" : "absent"
            }`}
          >
            {props.getValue() ? presentText : absentText}
          </div>
        ),
      }),
      columnHelper.accessor(
        (row) => {
          return getDateFromString(row?.curr_date);
        },
        {
          id: "Date",
          enableColumnFilter: false,
        }
      ),
    ],
  }),

  columnHelper.group({
    header: "Timing",
    columns: [
      columnHelper.accessor(
        (row) => {
          return row?.in_time ? formatTime(row?.in_time) : "-";
        },
        {
          id: "IN",
          enableColumnFilter: false,
        }
      ),
      columnHelper.accessor(
        (row) => {
          return row?.out_time
            ? row?.out_time === row?.in_time
              ? "-"
              : formatTime(row?.out_time)
            : "-";
        },
        {
          id: "OUT",
          enableColumnFilter: false,
        }
      ),
      columnHelper.accessor(
        (row) => {
          return convertDecimalToHoursMinutes(row?.working_hrs);
        },
        {
          id: "Working Hours",
          enableColumnFilter: false,
        }
      ),
      columnHelper.accessor((row) => row?.punches, {
        id: "Punches",
        enableColumnFilter: false,

        cell: (props) => {
          return props.getValue() > 1 && props.getValue() % 2 == 0 ? (
            <p style={{ color: "blue" }}> {props.getValue()} </p>
          ) : (
            <p style={{ color: "red" }}> {props.getValue()}</p>
          );
        },
      }),
    ],
  }),
];

export const AttendanceMonthColumns = [
  columnMonthHelper.accessor(
    (row) => {
      return getDateFromString(row.curr_date);
    },
    {
      id: "Date",
    }
  ),
  columnMonthHelper.accessor(
    (row) => {
      if (row?.in_time) {
        if (row?.working_hrs) {
          return row?.working_hrs;
        } else {
          return "0";
        }
      }
    },
    {
      id: "Status",
      cell: (props) => {
        const status = props.getValue() ? String(getP(props.getValue())) : "A";
        return (
          <div
            className={`attd-status attd-status--${getMonthStatusClass(
              status
            )}`}
          >
            {status}
          </div>
        );
      },
    }
  ),
  columnMonthHelper.accessor((row) => row?.in_time, {
    id: "IN",
    cell: (props) => {
      return props.getValue() ? formatTime(props.getValue()) : "-";
    },
  }),
  columnMonthHelper.accessor(
    (row) => {
      return row?.out_time
        ? row?.out_time === row?.in_time
          ? "-"
          : formatTime(row?.out_time)
        : "-";
    },
    {
      id: "OUT",
    }
  ),
  columnMonthHelper.accessor(
    (row) => {
      return convertDecimalToHoursMinutes(row?.working_hrs);
    },
    {
      header: "Working Hours",
    }
  ),
  columnHelper.accessor((row) => row?.punches, {
    header: "Punches",
    cell: (props) => {
      return props.getValue() > 1 && props.getValue() % 2 == 0 ? (
        <p style={{ color: "blue" }}> {props.getValue()} </p>
      ) : (
        <p style={{ color: "red" }}> {props.getValue()}</p>
      );
    },
  }),
];

export const terminatedEmployeeCols = [
  employeeColumnHelper.accessor((row) => row.emp_id, {
    header: "Employee ID",
    cell: (info) => info.getValue(),
  }),
  employeeColumnHelper.accessor((row) => row.emp_name, {
    header: "Employee Name",
    cell: (info) => info.getValue(),
  }),
  employeeColumnHelper.accessor((row) => row.updated_at, {
    header: "Termination Date",
    cell: (info) => getDateFromString(info.getValue()),
  }),
  employeeColumnHelper.accessor((row) => row.rate, {
    header: "Rate",
    cell: (info) => info.getValue(),
  }),
  employeeColumnHelper.accessor((row) => row.extra, {
    header: "Extra",
    cell: (info) => info.getValue(),
  }),
  employeeColumnHelper.accessor((row) => row.travel, {
    header: "Travel",
    cell: (info) => info.getValue(),
  }),
  employeeColumnHelper.accessor((row) => row.loan, {
    header: "Loan",
    cell: (info) => info.getValue(),
  }),
  employeeColumnHelper.accessor((row) => row.emp_id, {
    header: "Reactivate",
    cell: (props) => (
      <TerminateEmployeesButton empId={props.getValue().toString()} />
    ),
  }),
];
